import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { TASKS_PAGE } from '../../../lib/tasks-bi';
import { decisionOutcomeCopy } from '../../../lib/goal-ledger';
import { useDecisionAnswer } from '../../../lib/use-decision-answer';
import {
  promptAsDecision,
  promptQuestion,
  taskDecisionPrompts,
  taskDecisionShape,
  taskDecisionsUnavailable,
  type TaskDecisionChip,
} from '../../../lib/task-decisions';
import type { TaskDecisionPrompt } from '@wix/ai-scheduling-assistant-common-lib/utils';
import type { GoalOrigin } from '../../../lib/task-origin';
import { DecisionAnswerControls } from '../goals/DecisionAnswerBlock';
import { DECISION_ANSWER_LIMIT } from '../../../lib/decisions';
import { Button, TextButton } from '../ui';
import styles from './TaskDecisionPrompts.module.css';

/** The fields this block needs off a task row. Satisfied by `WebTask` (the list) and by `TaskDetail`
 *  (the drill-in) — deliberately the minimal set, not `WebTask` itself, so the same chips answer the
 *  same question wherever the task appears rather than needing a second implementation per shape. */
export interface TaskDecisionRowTask {
  id: string;
  goalId?: string;
  decisionPrompts?: TaskDecisionPrompt[];
  decisionPromptsUnavailable?: boolean;
}

/**
 * The question a task is blocked on, ANSWERED on the row that shows it.
 *
 * The row already carries the question (`readTaskDecisionPrompts`, common-lib). This is the other half:
 * one tap answers it, without working out which goal the task belongs to and navigating there.
 *
 * ## Three states, and the third is the point
 *
 * `decisionPrompts` present → the chips. `decisionPromptsUnavailable` → we say the question could not be
 * loaded and offer the goal. Neither → this component renders nothing and the row is exactly as it was.
 * Rendering the third state as the first gives somebody a task they can see they are needed for and can
 * never answer — a question that does not exist as far as the screen is concerned.
 *
 * ## It sits OUTSIDE the Card, and has to
 *
 * The card IS a button element (the whole row opens the task's detail), and a button nested inside a
 * button is invalid HTML that browsers resolve unpredictably. Same constraint that puts the row's
 * overflow menu and its goal-origin line outside the Card — and it reads correctly too: answering the
 * question is not a way of opening the task.
 *
 * ## There is ONE write path and ONE full control
 *
 * A chip commits through `useDecisionAnswer` → `POST /api/decision-verdict`, exactly as the goal ledger
 * does. `Answer this` opens the ledger's own control (`DecisionAnswerControls`) inline, sharing this
 * row's single flow — not a second control, and not a second answering state, because two flows for one
 * question would mint two verdict ids for one answer.
 */
export function TaskDecisionPrompts({
  task,
  prompts: promptsOverride,
  taskCount,
  origin,
  onAnsweredChange,
  page = TASKS_PAGE,
  pillChips = false,
  rowKey,
  className,
}: {
  task: TaskDecisionRowTask;
  /**
   * What the DOM ids are keyed off, when `task.id` cannot be (Q3). An OUTREACH question renders with
   * `task={{ id: taskId ?? '' }}` — often no task at all — so two outreach cards on one screen would mint
   * the same `aria-labelledby` id. The outreach surfaces pass the `decisionId`. Absent ≡ `task.id`.
   */
  rowKey?: string;
  /**
   * THE GROUPED ENTRY POINT — answer exactly these questions on this row, instead of everything `task`
   * carries. The agents island collapses one question blocking N tasks into ONE card
   * (`groupWaitingCards`) and hands that card its single prompt: without the override, a representative
   * task holding two questions would draw both of them on each of its two cards.
   *
   * Absent ≡ the task's own prompts, so the three per-task call sites (TaskCard, TaskDetailDesktop,
   * WorldAgentMenu) are byte-identical to before. It never ADDS a question: a prompt passed here is one
   * the caller read off a task row in the first place, and the write path is unchanged — the verdict
   * carries no task id at all (`buildVerdictBody`), so which row answered is a render-layer fact only.
   */
  prompts?: TaskDecisionPrompt[];
  /** How many tasks the card answering this question stands for — BI only (`task_count` on the
   *  `answer_decision_*` events, beside the representative `task_id`). Absent on a per-task row. */
  taskCount?: number;
  /** The task's goal, for the "could not load the question" state's way through. Null when unresolvable. */
  origin: GoalOrigin | null;
  /** Called with `true` once every question on this row has been answered — the row flips its own chip. */
  onAnsweredChange?: (allAnswered: boolean) => void;
  /** Where this row lives, for BI — the Tasks list by default. The drill-in passes `TASK_DETAILS_PAGE` so
   *  a tap here is not misattributed to a screen the reader is not on. */
  page?: { page_name: string; page_version?: string };
  /** The island quick-view card draws its answer chips as fully-rounded 26px PILLS (Figma 17339-142828).
   *  The Tasks list and the task-detail drill-in — the other two surfaces this SHARED block renders on —
   *  keep the default DS chip, so their screens are not restyled by an island-only frame. */
  pillChips?: boolean;
  /** Extra layout class for the block's own surface (the `.prompts` wrapper) — the ONLY way a call site may
   *  touch this shared block, used by the Tasks list to set the spacing BELOW the block to its next card.
   *  The island, the drill-in and the map home leave it unset, so their frames are untouched. */
  className?: string;
}) {
  const prompts = promptsOverride ?? taskDecisionPrompts(task);
  // A ref, not state: nothing HERE re-renders on it — the row above owns the chip it changes, and each
  // question's own settled sentence is that question's state.
  const answered = useRef<Set<string>>(new Set());

  // The tri-state's third state, and the override never reaches it: a row whose question could not be
  // READ carries no decision id, so it is never grouped into a card in the first place (`groupWaitingCards`
  // keeps it as its own row) and the flag is only ever set when no prompt arrived at all.
  if (!promptsOverride && taskDecisionsUnavailable(task)) {
    return <UnavailableNote origin={origin} className={className} />;
  }
  if (prompts.length === 0) return null;

  const onAnswer = (decisionId: string) => {
    answered.current.add(decisionId);
    // Only when the LAST one lands. A row holding two questions has not stopped needing the reader
    // because they answered one of them.
    onAnsweredChange?.(prompts.every((prompt) => answered.current.has(prompt.decisionId)));
  };

  // Every prompt, in the order the task names them. A row blocked on two questions shows two — hiding
  // the second would leave a task that stays blocked after an answer with no visible reason.
  return (
    <div className={className ? `${styles.prompts} ${className}` : styles.prompts}>
      {prompts.map((prompt, index) => (
        <TaskDecisionPromptRow key={prompt.decisionId} task={task} prompt={prompt} index={index} onAnswered={onAnswer} page={page} pillChips={pillChips} taskCount={taskCount} rowKey={rowKey} />
      ))}
    </div>
  );
}

function TaskDecisionPromptRow({
  task,
  prompt,
  index,
  onAnswered,
  page,
  pillChips,
  taskCount,
  rowKey,
}: {
  task: TaskDecisionRowTask;
  rowKey?: string;
  prompt: ReturnType<typeof taskDecisionPrompts>[number];
  index: number;
  page: { page_name: string; page_version?: string };
  onAnswered: (decisionId: string) => void;
  pillChips?: boolean;
  taskCount?: number;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const decision = useMemo(() => promptAsDecision(prompt), [prompt]);
  // ONE flow for this question, whichever shape answers it. The plan-version hold and the verdict pin
  // both live in here, and a second instance beside it would be a second pin — two ids for one answer.
  // An OUTREACH question often has no task behind it (`task={{ id: '' }}`): no task to invalidate and no
  // `task_id` on the BI — omitted, never `''` (rule 11). Its `outreachId` is what refreshes the contact
  // book after the answer (rule 12); read loosely, because a common-lib build predating the field still fits.
  const taskId = task.id || undefined;
  const outreachId = (prompt as { outreachId?: unknown }).outreachId;
  const taskBi = taskId ? { task_id: taskId } : {};
  const flow = useDecisionAnswer(decision, task.goalId, undefined, taskId, typeof outreachId === 'string' ? outreachId : undefined);
  const [expanded, setExpanded] = useState(false);
  // The optional note a refusal can carry ("Add a note" — matching iOS/Android). Offered only on the
  // approve/confirm chip shape (the one with a `refuse` chip); a CHOOSE picker has no single refusal here.
  // The note rides the refusal's `Verdict.answer`, so it needs no new field on the wire.
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');
  const shape = taskDecisionShape(prompt);
  // `task_count` rides `additionalFields` (the src:172 param set has no field for it) and is omitted
  // entirely on a per-task row, so an absent value means "not a grouped card" rather than one task.
  const biOpts = taskCount === undefined ? undefined : { additionalFields: { task_count: String(taskCount) } };
  const offersNote = shape.mode === 'chips' && shape.chips.some((chip) => chip.id === 'refuse');
  const questionId = `taskDecision-${rowKey ?? task.id}-${index}-question`;
  // A blank question is a real row (`Decision.question` is optional on the entity). It falls back to the
  // ledger's own "Waiting on you" — never to an unlabelled pair of buttons.
  const question = promptQuestion(prompt) ?? t('goals.ledger.open.title');

  // Read OFF THE FLOW, never assumed from the tap: only `ok: true` sets `answered`, and every refusal —
  // EXPIRED, WITHDRAWN, STALE_PLAN, a 5xx — leaves the question open and this silent. An effect rather
  // than a `.then` on submit, because `flow.answered` is state: a continuation would see the value from
  // the render that started the request, which is always null.
  useEffect(() => {
    if (flow.answered) onAnswered(prompt.decisionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow.answered, prompt.decisionId]);

  if (flow.answered) {
    // What the reader chose, in the same words the ledger will carry forever after — followed by what is
    // actually true next. The release is asynchronous and unbounded, so the row says the agent will pick
    // this up; it does NOT say the work started, and it never leaves a chip sitting there having changed
    // nothing. The list re-reads behind this (the flow invalidates `['tasks']`) and replaces it with the
    // authoritative row.
    const copy = decisionOutcomeCopy(flow.answered);
    return (
      <p className={styles.settled} aria-live="polite" data-testid={`taskDecisionSettled-${index}`}>
        <span className={styles.settledAnswer}>{t(copy.key, copy.params)}</span>{' '}
        <span className={styles.settledNext}>{t('tasks.decision.pickingUp')}</span>
      </p>
    );
  }

  const answer = (chip: TaskDecisionChip) => {
    logPageClickBi({ ...page, button_name: 'answer_decision_chip', ...taskBi, answer: prompt.decisionId }, biOpts);
    // A refusal carries the reader's words when they typed any (trimmed; `undefined` when blank so the
    // wire never stores an empty answer). Same rule as the ledger's `refusal()`.
    const value = chip.id === 'refuse' ? { ...chip.answer, answer: note.trim() || undefined } : chip.answer;
    void flow.submit(chip.id, value);
  };

  const disabled = flow.busy !== null || flow.effect?.retryable === false;

  return (
    <div className={pillChips ? `${styles.prompt} ${styles.promptPill}` : styles.prompt}>
      <p className={styles.question} id={questionId} dir="auto">
        {question}
      </p>
      {shape.mode === 'unanswerable' ? (
        // A CHOOSE that arrived with no options. An empty picker is not a smaller picker; it is a
        // question with no answers, and synthesising Approve/Decline would send a verdict in a vocabulary
        // this decision does not have.
        <span className={styles.note}>{t('goals.decision.unanswerable')}</span>
      ) : shape.mode === 'chips' ? (
        <>
          {/* Figma 17339-142828 "Symphony Button Round": fully-rounded 26px outlined pills. "Add a note"
              sits BESIDE the chips (matching native), not below — opening it reveals the field underneath. */}
          <div className={styles.chips} role="group" aria-labelledby={questionId}>
            {shape.chips.map((chip) => (
              <Button
                key={chip.id}
                variant="outlined"
                size={pillChips ? 'tiny' : 'small'}
                className={styles.chip}
                style={pillChips ? { borderRadius: 9999, minHeight: 26, paddingInline: 12 } : undefined}
                data-testid={`taskDecisionChip-${index}-${chip.id}`}
                // Always the full label, not only when `chipLabel` clamped it. The chip now ellipsizes on
                // WIDTH too (see `.chip` in the module), so a label well under the 28-character clamp can
                // still be cut on a narrow phone — and a tooltip that only appears for the clamped ones
                // leaves exactly the visually-truncated labels unreadable. `fullLabel` is present only when
                // the clamp fired, so the unclamped case falls back to the label itself.
                title={chip.fullLabel ?? (chip.labelKey ? t(chip.labelKey) : chip.label)}
                loading={flow.busy === chip.id}
                disabled={disabled}
                onClick={() => answer(chip)}
              >
                <span dir="auto">{chip.labelKey ? t(chip.labelKey) : chip.label}</span>
              </Button>
            ))}
            {/* A refusal can carry the reader's words. Offered only on the approve/confirm shape; it never
                gates the answer (the Decline chip submits with or without a note). */}
            {offersNote && !noteOpen && (
              // Pushed to the trailing edge of the chip row (marginLeft:auto) — "Add a note" sits to the
              // RIGHT of the chips on all three surfaces.
              <span style={{ marginLeft: 'auto' }}>
                <TextButton
                  size="small"
                  underline
                  data-testid={`decisionRefuseNote-${index}`}
                  disabled={disabled}
                  onClick={() => {
                    logPageClickBi({ ...page, button_name: 'open_decision_note', ...taskBi, answer: prompt.decisionId }, biOpts);
                    setNoteOpen(true);
                  }}
                >
                  {t('goals.decision.refuseNote')}
                </TextButton>
              </span>
            )}
          </div>
          {offersNote && noteOpen && (
            <textarea
              className={styles.refuseNoteField}
              data-testid={`decisionRefuseNoteField-${index}`}
              value={note}
              onChange={(event) => setNote(event.target.value.slice(0, DECISION_ANSWER_LIMIT))}
              placeholder={t('goals.decision.refuseNotePlaceholder')}
              maxLength={DECISION_ANSWER_LIMIT}
              rows={2}
              disabled={disabled}
              dir="auto"
              autoFocus
            />
          )}
        </>
      ) : expanded ? (
        // The ledger's own control, in place. Not a second implementation and not a navigation step —
        // and it shares the flow above, so the answer that lands here is the same answer this row knows
        // about.
        <DecisionAnswerControls decision={decision} questionId={questionId} index={index} flow={flow} biPage={page.page_name} />
      ) : (
        <div className={styles.chips}>
          <Button
            variant="outlined"
            size="small"
            className={styles.chip}
            data-testid={`taskDecisionAnswerThis-${index}`}
            onClick={() => {
              logPageClickBi({ ...page, button_name: 'open_decision_control', ...taskBi, answer: prompt.decisionId }, biOpts);
              setExpanded(true);
            }}
          >
            {t('tasks.decision.answerThis')}
          </Button>
        </div>
      )}
      {flow.effect && (
        <span className={flow.effect.tone === 'danger' ? styles.error : styles.note} data-testid={`taskDecisionError-${index}`} role="alert">
          {t(flow.effect.messageKey)}
        </span>
      )}
    </div>
  );
}

/**
 * The third state: this task names a blocker we could not resolve into a question — the decision read
 * failed, or the batch cap dropped it.
 *
 * It must not read as "nothing to answer". A failed decoration that renders as state 1 is a question the
 * user can never answer, on the one screen built to tell them they are needed — so the row says what
 * happened and offers the goal, which is where the question can still be answered in full.
 */
function UnavailableNote({ origin, className }: { origin: GoalOrigin | null; className?: string }) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  return (
    <div className={className ? `${styles.prompts} ${className}` : styles.prompts}>
      <p className={styles.unavailable}>
        {t('tasks.decision.unavailable')}
        {origin?.href ? (
          <>
            {' '}
            <Link href={`${basePath}${origin.href}`} className={styles.goalLink} onClick={(event) => event.stopPropagation()}>
              {t('tasks.decision.openGoal')}
            </Link>
          </>
        ) : null}
      </p>
    </div>
  );
}
